"use client";
import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import Link from "next/link";

import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import ButtonLink from "@/next-src/components/ui/button-link/ButtonLink";
import BrochureModal from "@/next-src/components/home/brochure/brochure-modal/BrochureModal";
import { useBrochureUpdate } from "@/next-src/context/Brochure";
import ResponsiveImage from "@/next-src/components/ui/responsive-image/ResponsiveImage";
import Calendly from "@/next-src/components/ui/calendly/Calendly";
import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";

import comCSS from "./Footer.module.scss";

const Footer = ({ className = "", data = {}, brochureModal = {} }) => {
  const { setIsShown } = useBrochureUpdate();
  const { width: windowWidth } = useWindowDimensions();
  const {
    taglineMobile,
    button,
    buttonMobile,
    rightsTextMobile,
    textNoteMobile,
    bgColorMobile,
    logo,
    menu,
    calendlyButtonSuccess,
    calendlyButtonSuccessMobile,
  } = data || {};
  let { tagline, rightsText, textNote, bgColor } = data || {};

  if (windowWidth < 992) {
    tagline = taglineMobile;
    rightsText = rightsTextMobile;
    textNote = textNoteMobile;
    bgColor = bgColorMobile;
  }
  const getText = (el, keyLG, keyXS) => {
    if (windowWidth < 992) {
      return convertHTMLtoJSX(el?.[keyXS]);
    }
    return convertHTMLtoJSX(el?.[keyLG]);
  };
  return (
    <>
      <BrochureModal data={brochureModal} />
      <div className={cn(className, comCSS.wrapper, "footer")}>
        <div
          className="padding-global"
          style={{
            backgroundColor: bgColor,
          }}
        >
          <div className="container-large">
            <div className="padding-vertical padding-xxlarge">
              <div className="padding-bottom padding-footer">
                <div className="w-layout-grid footer1_top-wrapper">
                  <div className="footer1_left-wrapper">
                    <div className="margin-bottom margin-small">
                      <a href="#" className="footer1_logo-link w-nav-brand">
                        <ResponsiveImage
                          width={200}
                          height={50}
                          src={logo?.src}
                          lazyLoading
                          alt={logo?.alt}
                          sizes="154px"
                        />
                      </a>
                    </div>
                    {tagline ? (
                      <div className="margin-bottom margin-small">
                        <div className="text-weight-normal">
                          {convertHTMLtoJSX(tagline)}
                        </div>
                      </div>
                    ) : null}

                    {button.calendlyLink ? (
                      <Calendly
                        classNameBtn="is-icon is-small nav-btn"
                        classNameBtnSuccess="is-icon is-small nav-btn"
                        isPulsingIcon
                        data={{
                          calendlyButton: button,
                          calendlyButtonMobile: buttonMobile,
                          calendlyButtonSuccess,
                          calendlyButtonSuccessMobile,
                        }}
                      />
                    ) : (
                      <ButtonLink
                        data={button}
                        isPulsingIcon
                        className="is-icon is-small nav-btn"
                      />
                    )}
                  </div>
                  <div className="w-layout-grid footer1_menu-wrapper">
                    {menu?.slice(0, -1).map((el) => (
                      <div key={el.id} className="footer1_link-list">
                        <div className="margin-bottom margin-xsmall">
                          <div className="footer-category-name">
                            {getText(el, "title", "titleMobile")}
                          </div>
                        </div>
                        {el?.children
                          ?.filter((link) => link.isActive)
                          ?.map((link) => (
                            <Link
                              key={link.id}
                              href={link.href}
                              className={cn({
                                footer1_link: !link.icon?.src,
                                "w--current": link.isActive,
                                "footer1_social-link w-inline-block":
                                  link.icon?.src,
                              })}
                              onClick={(e) => {
                                if (link.href === "#brochure") {
                                  e.preventDefault();
                                  setIsShown(true);
                                }
                              }}
                            >
                              {link.icon?.src ? (
                                <div className="footer1_social-icon w-embed">
                                  <ResponsiveImage
                                    src={link.icon?.src}
                                    alt={link.icon?.alt}
                                    width={24}
                                    height={24}
                                    sizes="24px"
                                  />
                                </div>
                              ) : null}
                              {getText(link, "title", "titleMobile")}
                            </Link>
                          ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="line-divider"></div>
              <div className="text-note vertical-margins">
                {convertHTMLtoJSX(textNote)}
              </div>
              <div className="line-divider"></div>
              <div className="padding-top padding-medium">
                <div className="footer1_bottom-wrapper">
                  <div className={cn("footer1_credit-text", comCSS.rights)}>
                    © {new Date().getFullYear()}&nbsp;
                    {convertHTMLtoJSX(rightsText)}
                  </div>
                  <div className="w-layout-grid footer1_legal-list">
                    {menu?.slice(-1)?.[0]?.children?.map((el) => (
                      <Link
                        key={el.id}
                        href={el?.href}
                        className="footer1_legal-link"
                      >
                        {getText(el, "title", "titleMobile")}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

Footer.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    logo: PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    }),
    tagline: PropTypes.string,
    taglineMobile: PropTypes.string,
    button: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
      calendlyLink: PropTypes.string,
    }),
    buttonMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
      calendlyLink: PropTypes.string,
    }),
    calendlyButtonSuccess: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
      calendlyLink: PropTypes.string,
    }),
    calendlyButtonSuccessMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
      calendlyLink: PropTypes.string,
    }),
    menu: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        title: PropTypes.string,
        titleMobile: PropTypes.string,
        children: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            href: PropTypes.string,
            title: PropTypes.string,
            titleMobile: PropTypes.string,
            isActive: PropTypes.bool,
            icon: PropTypes.shape({
              src: PropTypes.string,
              alt: PropTypes.string.isRequired,
            }),
          })
        ),
      })
    ),
    rightsText: PropTypes.string,
    rightsTextMobile: PropTypes.string,
    textNote: PropTypes.string,
    textNoteMobile: PropTypes.string,
    bgColor: PropTypes.string,
    bgColorMobile: PropTypes.string,
  }),
  brochureModal: PropTypes.shape({}),
};

export default Footer;
